import React from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { AdminBonus2 } from "services/bonus/type"

import { BONUS_TYPE_OPTIONS, TypeChangeEffect } from "../../helpers/bonus2.helpers"

interface TypeChangeConfirmProps {
  type: AdminBonus2["type"]
  effects: TypeChangeEffect[]
  onConfirm: () => void
  onCancel: () => void
}

/** `wagering_base` → "Wagering base". Derived rather than a hand-kept label map */
const humanizeField = (field: string): string => field.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())

const formatValue = (v: unknown): string => {
  if (v === undefined || v === null || v === "") return "—"
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`
  if (typeof v === "object") return "—"
  return String(v).replace(/_/g, " ")
}

/**
 * Shown BEFORE a type change is applied: it rewrites the type's recommended config and hides what
 * the new type cannot use, and none of it returns if the operator changes their mind.
 *
 * Rendered as an overlay inside the form rather than a nested <Modal> — a nested one carries its
 * own Escape handler, and Escape on the form's modal discards every unsaved edit.
 */
export const TypeChangeConfirm: React.FC<TypeChangeConfirmProps> = ({ type, effects, onConfirm, onCancel }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const typeOption = BONUS_TYPE_OPTIONS.find((o) => o.value === type)
  const typeName = typeOption ? (t(typeOption.label) as string) : type

  const actions = [
    { key: "cancel", label: t("bonus2.form.cancel"), className: "btn-secondary", onClick: onCancel },
    { key: "confirm", label: t("bonus2.typeChange.confirm"), className: "btn-primary", onClick: onConfirm }
  ]

  const cellsFor = (effect: TypeChangeEffect) => [
    { key: "field", text: humanizeField(effect.field), className: "dark:text-white" },
    { key: "from", text: effect.detail ?? formatValue(effect.from), className: "text-gray-400" },
    { key: "arrow", text: "→", className: "text-gray-500" },
    {
      key: "to",
      text: effect.dropped
        ? (t("bonus2.typeChange.removed") as string)
        : effect.reenter
          ? (t("bonus2.typeChange.reenter") as string)
          : formatValue(effect.to),
      className: effect.dropped || effect.reenter ? "text-amber-400" : "dark:text-white"
    }
  ]

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 p-6">
      <div className="flex max-h-full w-full max-w-xl flex-col rounded-xl bg-base-100 p-6 shadow-xl">
        <h3 className="text-lg font-semibold dark:text-white">{t("bonus2.typeChange.title", { type: typeName })}</h3>
        <p className="mt-2 text-sm text-gray-400">{t("bonus2.typeChange.intro")}</p>

        <div className="mt-4 min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-700">
          <table className="w-full text-sm">
            <tbody>
              {effects.map((effect) => (
                <tr key={effect.field} className="border-b border-gray-700 last:border-0">
                  {cellsFor(effect).map((cell) => (
                    <td key={cell.key} className={clsx("px-3 py-2", cell.className)}>
                      {cell.text}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-sm text-amber-400">{t("bonus2.typeChange.notRestored")}</p>

        <div className="modal-action">
          {actions.map((action) => (
            <button key={action.key} type="button" className={clsx("btn", action.className)} onClick={action.onClick}>
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
